
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SecurityEnhancedAuth } from './SecurityEnhancedAuth';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from "@/lib/utils";
import { Loader2 } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email').min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

interface LoginFormProps {
  onForgotPassword?: () => void;
}

export function LoginForm({ onForgotPassword }: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const form = useForm<{ email: string; password: string }>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: { email: string; password: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await SecurityEnhancedAuth.signInWithPassword(data.email, data.password);

      if (authError) {
        let errorMessage = 'Sign in failed. Please check your credentials.';

        if (authError.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (authError.message.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address before signing in.';
        } else if (authError.message.includes('Too many requests')) {
          errorMessage = 'Too many login attempts. Please wait before trying again.';
        } else if (authError.message.includes('Too many login attempts')) {
          errorMessage = 'Account temporarily locked due to too many failed attempts.';
        }

        setError(errorMessage);
        return;
      }

      // Redirect will happen automatically via auth state change

    } catch (error: any) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div
            className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
            role="alert"
            aria-live="polite"
          >
            {error}
          </div>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="modern-text-caption font-medium">Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your email"
                  {...field}
                  type="email"
                  autoComplete="email"
                  className={cn(
                    "transition-all focus-visible:ring-2 focus-visible:ring-primary bg-background border-border",
                    isMobile ? "h-12 text-base px-4" : ""
                  )}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="modern-text-caption font-medium">Password</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your password"
                  {...field}
                  type="password"
                  autoComplete="current-password"
                  className={cn(
                    "transition-all focus-visible:ring-2 focus-visible:ring-primary bg-background border-border",
                    isMobile ? "h-12 text-base px-4" : ""
                  )}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        
        <div className="flex flex-col space-y-3 pt-2">
          <Button
            type="submit"
            variant="default"
            className="w-full"
            size={isMobile ? "lg" : "default"}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </Button>

          {onForgotPassword && (
            <Button
              type="button"
              variant="link"
              onClick={onForgotPassword}
              className="text-primary hover:text-primary/80 p-0 h-auto font-medium"
            >
              Forgot your password?
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
